import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mergeMap, switchMap, map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { CylindersActions } from './cylinders.actions';
import { CylinderService } from '../../services/cylinder.service';

@Injectable()
export class CylindersEffects {
  private actions$ = inject(Actions);
  private svc = inject(CylinderService);

  loadTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CylindersActions.loadCylinderTypes),
      mergeMap(() => this.svc.getAllTypes().pipe(
        map(types => CylindersActions.loadCylinderTypesSuccess({ types })),
        catchError(err => of(CylindersActions.loadCylinderTypesFailure({ error: err.message })))
      ))
    )
  );

  createType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CylindersActions.createCylinderType),
      mergeMap(({ dto }) => this.svc.createType(dto).pipe(
        map(cylinderType => CylindersActions.createCylinderTypeSuccess({ cylinderType })),
        catchError(err => of(CylindersActions.createCylinderTypeFailure({ error: err.message })))
      ))
    )
  );

  updateType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CylindersActions.updateCylinderType),
      mergeMap(({ id, dto }) => this.svc.updateType(id, dto).pipe(
        map(cylinderType => CylindersActions.updateCylinderTypeSuccess({ cylinderType })),
        catchError(err => of(CylindersActions.updateCylinderTypeFailure({ error: err.message })))
      ))
    )
  );

  deleteType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CylindersActions.deleteCylinderType),
      mergeMap(({ id }) => this.svc.deleteType(id).pipe(
        map(() => CylindersActions.deleteCylinderTypeSuccess({ id })),
        catchError(err => of(CylindersActions.deleteCylinderTypeFailure({ error: err.message })))
      ))
    )
  );

  loadCylinders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CylindersActions.loadCylinders),
      mergeMap(() => this.svc.getAll().pipe(
        map(cylinders => CylindersActions.loadCylindersSuccess({ cylinders })),
        catchError(err => of(CylindersActions.loadCylindersFailure({ error: err.message })))
      ))
    )
  );

  purchaseCylinder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CylindersActions.purchaseCylinder),
      mergeMap(({ dto }) => this.svc.purchase(dto).pipe(
        map(cylinder => CylindersActions.purchaseCylinderSuccess({ cylinder })),
        catchError(err => of(CylindersActions.purchaseCylinderFailure({ error: err.message })))
      ))
    )
  );

  decommissionCylinder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CylindersActions.decommissionCylinder),
      mergeMap(({ id, performedByPersonnelId, notes }) => this.svc.decommission(id, performedByPersonnelId, notes).pipe(
        map(cylinder => CylindersActions.decommissionCylinderSuccess({ cylinder })),
        catchError(err => of(CylindersActions.decommissionCylinderFailure({ error: err.message })))
      ))
    )
  );

  loadMovements$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CylindersActions.loadCylinderMovements),
      switchMap(({ cylinderId }) => this.svc.getMovements(cylinderId).pipe(
        map(movements => CylindersActions.loadCylinderMovementsSuccess({ cylinderId, movements })),
        catchError(err => of(CylindersActions.loadCylinderMovementsFailure({ error: err.message })))
      ))
    )
  );

  clearCylinderCustody$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CylindersActions.clearCylinderCustody),
      mergeMap(({ id, workOrderId, dto }) => this.svc.clearCustody(id, dto).pipe(
        map(cylinder => CylindersActions.clearCylinderCustodySuccess({ workOrderId, cylinder })),
        catchError(err => of(CylindersActions.clearCylinderCustodyFailure({ error: err.message })))
      ))
    )
  );
}
